/**
 * Admin route guard (admin-auth-fix 2026-06-16, Option A).
 *
 * Wraps the in-app `/admin` route. Composition:
 *   1. `AuthGuard` enforces authentication + approval and reuses the LIVE Clerk
 *      session (getToken()) — there is NO second sign-in. An anonymous user is
 *      redirected to `/sign-in` by AuthGuard; a pending user to `/pending-approval`.
 *   2. The inner `AdminOnly` then enforces the admin role: a signed-in, approved
 *      NON-admin is redirected to `/chat` and never sees the admin shell.
 *
 * SECURITY: the client `isAdmin` flag is UX ONLY. The engine `requireAdmin`
 * middleware (401/403) is the real authorization gate on every admin surface
 * (static serve + page REST). This guard only avoids rendering an admin shell to
 * a user the server would reject anyway — it never grants access.
 *
 * @example
 * ```tsx
 * <AdminGuard>
 *   <AdminDashboardPage />
 * </AdminGuard>
 * ```
 */
import { useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../shared/stores/auth'
import { AuthGuard } from './AuthGuard'

interface AdminGuardProps {
  /** Admin-only content to render when authenticated, approved, and admin. */
  children: ReactNode
}

function AdminOnly({ children }: AdminGuardProps) {
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const navigate = useNavigate()

  // Non-admin (signed-in, approved) → bounce to chat. UX redirect only; the
  // engine 403 is the authoritative gate if a request is ever made directly.
  useEffect(() => {
    if (!isAdmin) {
      navigate('/chat', { replace: true })
    }
  }, [isAdmin, navigate])

  if (!isAdmin) {
    return null
  }

  return <>{children}</>
}

export function AdminGuard({ children }: AdminGuardProps) {
  return (
    <AuthGuard>
      <AdminOnly>{children}</AdminOnly>
    </AuthGuard>
  )
}
